import React, { useEffect, useRef, useState } from 'react';
import { Alert, Avatar, Button, Card, Divider, Empty, Space, Spin } from 'antd';
import { DownloadOutlined } from '@ant-design/icons';
import { useContractApi } from 'api/contract';
import { firstLetters } from 'shared/Helpers';
import {
  EML_MAX_PARSE_BYTES,
  parseEmlDeliverable,
  type DeliverableAttachment,
  type ParsedEmlDeliverable
} from './emlAdapter';
import { parseMsgDeliverable } from './msgAdapter';
import { downloadAttachment, formatAttachmentSize } from './attachmentDownload';

/**
 * The attachments carried inside the message, offered as downloads.
 *
 * ⚠️ These are files embedded in the uploaded email, NOT deliverables in their own right — they have
 * no `deliverable` row and are reachable from no other surface, which is exactly why the viewer has
 * to offer them. Inline images are deliberately absent: the adapter has already resolved those into
 * the body, and listing them here would offer the reader the same picture twice.
 * @param {object} props - Component props.
 * @param {DeliverableAttachment[]} props.attachments - The attachments to offer.
 * @returns {React.ReactElement | null} The list, or null when the message carries none.
 */
function EmailAttachmentList({ attachments }: { attachments: DeliverableAttachment[] }): React.ReactElement | null {
  if (attachments.length === 0) return null;

  return (
    <>
      <Divider className="m-0 mt-5" />
      <section aria-labelledby="email-attachments-title">
        <div id="email-attachments-title" className="text-sm mb-2">
          {`Attachments (${attachments.length})`}
        </div>
        <Space direction="vertical" size={4} className="w-full">
          {attachments.map((attachment, index) => (
            <Button
              // The file name is not a key: a message may legitimately carry two parts with the
              // same name, and duplicate keys would drop one from the list.
              key={`${attachment.fileName}-${index}`}
              type="link"
              icon={<DownloadOutlined />}
              className="p-0 h-auto text-left"
              onClick={() => downloadAttachment(attachment)}
            >
              <span className="mr-2">{attachment.fileName || '(unnamed attachment)'}</span>
              <span className="text-xs text-gray-500">{formatAttachmentSize(attachment.sizeBytes)}</span>
            </Button>
          ))}
        </Space>
      </section>
    </>
  );
}

/**
 * Which email format the bytes are, and therefore which parser reads them.
 *
 * ⚠️ This is NOT cosmetic and must never be guessed or defaulted. `.eml` is RFC 5322 text and
 * `.msg` is an OLE compound file; each parser handed the other's bytes yields a plausible-looking
 * EMPTY message rather than an error, so a wrong `kind` fails silently. It is set from the routing
 * arm (`resolveDeliverableViewerArm`), which is the single source of truth for what a file is.
 */
export type EmailDeliverableKind = 'eml' | 'msg';

/** The parser for each kind. Both produce `ParsedEmlDeliverable`, which is the shared contract. */
const PARSER_BY_KIND = {
  eml: parseEmlDeliverable,
  msg: parseMsgDeliverable
} as const;

/** How much chrome a kind draws around a message whose fields are sparse. */
interface KindPresentation {
  /**
   * Omit a header field entirely when it has no value, rather than drawing an em-dash placeholder.
   *
   * True only for `msg`, because the library supplies less: `cc` is NEVER populated and `date` is
   * absent whenever Outlook omitted transport headers (routine on sent and drafted mail), so the
   * placeholder treatment renders a column of em-dashes on a perfectly good message.
   */
  hidesEmptyFields: boolean;
  /**
   * Disclose embedded messages the parse could not render.
   *
   * ⚠️ False for `msg` by OWNER DECISION, and it trades away disclosure: the adapter still counts
   * them, but a `.msg` carrying embedded emails now previews with no sign they exist. Kept true for
   * `eml`, where dropping them silently is prohibited.
   */
  showsForwardedMarker: boolean;
}

/**
 * Per-kind presentation. ⚠️ `eml`'s entry is the SHIPPED behaviour and must stay that way — the
 * sparse-field change was asked for on `msg` only. Keying it here rather than editing the shared
 * render is what keeps the divergence from leaking into `.eml`.
 */
const PRESENTATION_BY_KIND: Record<EmailDeliverableKind, KindPresentation> = {
  eml: { hidesEmptyFields: false, showsForwardedMarker: true },
  msg: { hidesEmptyFields: true, showsForwardedMarker: false }
};

export interface EmlDeliverableViewerProps {
  /**
   * Which parser to use. Required with no default: an omitted `kind` would have to fall back to one
   * of the two, and that fallback would render an empty message for every file of the other format.
   */
  kind: EmailDeliverableKind;
  /** Presigned S3 URL for the message bytes. */
  downloadUrl?: string;
  /** Original file name, shown on the download affordance. */
  fileName: string;
  /**
   * Size from the list DTO, which declares it optional *and* nullable. Unknown size still means
   * parse, not block — but the key is **required** here, with no `?`, so a call site that forgets
   * it fails to compile rather than silently disabling the pre-fetch half of the size guard.
   * Passing `fileSizeBytes={undefined}` explicitly stays legal and means "size unknown".
   */
  fileSizeBytes: number | null | undefined;
}

type ViewerState =
  | { status: 'loading' }
  | { status: 'error' }
  | { status: 'oversize' }
  | { status: 'empty' }
  | { status: 'ready'; parsed: ParsedEmlDeliverable };

/**
 * Fetches the message bytes and adapts them for rendering.
 *
 * The fetcher comes from `useContractApi`, which builds a fresh axios instance every render; a
 * latest-value ref keeps it out of the effect deps so the effect re-runs only on real input changes
 * or an explicit retry. `getBlobFromFileURL` swallows network errors through `AxiosErrorHandler` and
 * returns `undefined`, so a failure resolves rather than rejects — both paths are handled.
 *
 * The size ceiling is checked twice: against the DTO's `fileSizeBytes` **before** fetching, so an
 * oversize message is never fetched or parsed, and against `blob.size` after, which closes the
 * unknown-size path to a main-thread freeze without making unknown size mean "block".
 * @param {EmlDeliverableViewerProps & { attempt: number }} input - Document inputs plus a retry counter.
 * @returns {ViewerState} The current fetch/parse state.
 */
function useParsedEmlDeliverable({
  kind,
  downloadUrl,
  fileSizeBytes,
  attempt
}: EmlDeliverableViewerProps & { attempt: number }): ViewerState {
  const { getBlobFromFileURL } = useContractApi();
  const fetchRef = useRef(getBlobFromFileURL);
  fetchRef.current = getBlobFromFileURL;
  const [state, setState] = useState<ViewerState>({ status: 'loading' });

  useEffect(() => {
    if (fileSizeBytes != null && fileSizeBytes > EML_MAX_PARSE_BYTES) {
      setState({ status: 'oversize' });
      return;
    }
    if (!downloadUrl) {
      setState({ status: 'error' });
      return;
    }
    let cancelled = false;
    setState({ status: 'loading' });
    fetchRef
      .current(downloadUrl)
      .then(async (blob) => {
        if (cancelled) return;
        if (!blob) {
          setState({ status: 'error' });
          return;
        }
        if (blob.size > EML_MAX_PARSE_BYTES) {
          setState({ status: 'oversize' });
          return;
        }
        const parsed = await PARSER_BY_KIND[kind](blob);
        if (cancelled) return;
        setState(parsed.isEmpty ? { status: 'empty' } : { status: 'ready', parsed });
      })
      .catch(() => {
        if (!cancelled) setState({ status: 'error' });
      });
    return () => {
      cancelled = true;
    };
    // `kind` is a dep because a surface can swap documents without unmounting: the viewer modal
    // renders one body per open, but `doc` changing while open must re-parse with the right parser.
  }, [kind, downloadUrl, fileSizeBytes, attempt]);

  return state;
}

/**
 * Initials for the sender avatar, taken from the display name rather than the whole `From` string —
 * `firstLetters('Analyst <analyst@npi…>')` would otherwise read the `<` as a word.
 * @param {string} from - The adapter's formatted `From` value.
 * @returns {string} One or two initials, or '' when there is no name.
 */
function senderInitials(from: string): string {
  const name = from.split('<')[0].trim();
  return firstLetters(name);
}

/**
 * The marker for forwarded (`message/rfc822`) parts the adapter dropped. Dropping them silently is
 * prohibited: the loss is substance, not decoration.
 * @param {number} count - `ParsedEmlDeliverable.forwardedMessageCount`, always > 0 when rendered.
 * @returns {string} The marker copy.
 */
export function forwardedMarkerLabel(count: number): string {
  const subject = count === 1 ? '1 forwarded message is' : `${count} forwarded messages are`;
  return `${subject} attached to this email and not shown here. Download the email to read them.`;
}

/**
 * A download-instead-of-preview affordance, used by the oversize and unreadable states.
 *
 * When no presigned URL is available there is nothing to download, so an inline error replaces copy
 * that would otherwise tell the reader to download a file no button offers. That state is reachable:
 * the size guard runs *before* the URL check, so an oversize `.eml` whose presign failed lands here,
 * and `downloadUrl` is nullable on the DTO. The copy follows `DownloadDeliverableFallback`'s existing
 * no-URL pattern rather than inventing its own.
 * @param {object} props - Component props.
 * @param {string} props.description - Why the message is not being previewed.
 * @param {string} [props.downloadUrl] - Presigned S3 URL, when one is available.
 * @returns {React.ReactElement} The affordance, or an inline error state.
 */
function DownloadInstead({
  description,
  downloadUrl
}: {
  description: string;
  downloadUrl?: string;
}): React.ReactElement {
  if (!downloadUrl) {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <Alert
          type="error"
          showIcon
          message="Unable to download deliverable"
          description="No download URL is available for this deliverable."
        />
      </div>
    );
  }

  return (
    <div className="h-full flex items-center justify-center p-6">
      <Empty description={description}>
        <Button
          type="primary"
          icon={<DownloadOutlined />}
          onClick={() => window.open(downloadUrl, '_blank', 'noopener,noreferrer')}
        >
          Download
        </Button>
      </Empty>
    </div>
  );
}

/**
 * The header block — From / To / Cc. It is part of the evidentiary record, not chrome: `Cc` is
 * shown, and the `Date` beside it carries the source message's own UTC offset.
 *
 * Under `hidesEmptyFields` a valueless row is omitted rather than drawn as an em-dash, and a
 * message with no From, To or Cc at all yields **null** — no title block, so the card renders the
 * body alone. That is the `.msg` treatment; `.eml` keeps its placeholders.
 * @param {object} props - Component props.
 * @param {ParsedEmlDeliverable} props.parsed - The adapted message.
 * @param {boolean} props.hidesEmptyFields - Omit valueless rows instead of placeholdering them.
 * @returns {React.ReactElement | null} The card title block, or null when there is nothing to show.
 */
function EmlHeaderBlock({
  parsed,
  hidesEmptyFields
}: {
  parsed: ParsedEmlDeliverable;
  hidesEmptyFields: boolean;
}): React.ReactElement | null {
  const showsFrom = !hidesEmptyFields || parsed.from !== '';
  const showsTo = !hidesEmptyFields || parsed.to !== '';

  // Nothing identifies the message — drop the block rather than draw an empty avatar beside it.
  if (!showsFrom && !showsTo && parsed.cc === '') return null;

  return (
    <Space>
      <Avatar size="large" className="bg-[#152D51] flex-none">
        {senderInitials(parsed.from) || null}
      </Avatar>
      <div>
        {showsFrom ? <div>From: {parsed.from || '—'}</div> : null}
        {showsTo ? <div className="text-sm font-normal">To: {parsed.to || '—'}</div> : null}
        {parsed.cc ? <div className="text-sm font-normal">Cc: {parsed.cc}</div> : null}
      </div>
    </Space>
  );
}

/**
 * Renders an uploaded email deliverable — `.eml` or `.msg`, per `kind` — as the header block plus
 * the message body.
 *
 * ⚠️ The file is still named for `.eml` while it serves both formats. That is deliberate: the
 * component was generalised in place rather than renamed, so the diff stays confined to the parser
 * selection and no import or test file churns. `kind` is the only thing that varies.
 *
 * **The empty `sandbox` attribute is load-bearing, not defence-in-depth.** The eml adapter's output
 * is not sanitised for script execution as a second layer, so any value at all in that attribute
 * makes this an XSS sink. It also does not block image loads — the adapter's parse-time strip is the
 * only defence against remote content. (The msg adapter never returns `bodyHtml`, so `kind: 'msg'`
 * takes the plain-text branch and never reaches the frame at all.)
 *
 * `EML_MAX_PARSE_BYTES` is the ceiling for BOTH kinds rather than one per format: msgreader's parse
 * is main-thread and linear in the same way postal-mime's is, so the hazard the ceiling exists for
 * is identical and a second constant would only be a second thing to keep in step.
 * @param {EmlDeliverableViewerProps} props - The kind, presigned URL, file name, and size.
 * @returns {React.ReactElement} The message, or a loading/error/empty/oversize state.
 */
export function EmlDeliverableViewer(props: EmlDeliverableViewerProps): React.ReactElement {
  const [attempt, setAttempt] = useState(0);
  const state = useParsedEmlDeliverable({ ...props, attempt });

  if (state.status === 'loading') {
    return (
      <div className="h-full flex items-center justify-center">
        <Spin size="large" />
      </div>
    );
  }

  if (state.status === 'error') {
    return (
      <div className="h-full flex items-center justify-center p-6">
        <Alert
          type="error"
          showIcon
          message="Unable to load deliverable"
          description="The email could not be fetched."
          action={
            <Button size="small" onClick={() => setAttempt((n) => n + 1)}>
              Retry
            </Button>
          }
        />
      </div>
    );
  }

  if (state.status === 'oversize') {
    return (
      <DownloadInstead
        downloadUrl={props.downloadUrl}
        description={`This email is too large to preview. Download ${props.fileName} to open it.`}
      />
    );
  }

  if (state.status === 'empty') {
    return (
      <DownloadInstead
        downloadUrl={props.downloadUrl}
        description={`This file could not be read as an email. Download ${props.fileName} to open it.`}
      />
    );
  }

  const { parsed } = state;
  const { hidesEmptyFields, showsForwardedMarker } = PRESENTATION_BY_KIND[props.kind];
  // `undefined` rather than an em-dash node, so antd draws no `extra` slot at all.
  const dateNode = parsed.date !== '' || !hidesEmptyFields ? <div>{parsed.date || '—'}</div> : undefined;
  const showsSubject = !hidesEmptyFields || parsed.subject !== '';

  return (
    <div className="h-full overflow-auto">
      <Card
        bordered
        title={<EmlHeaderBlock parsed={parsed} hidesEmptyFields={hidesEmptyFields} />}
        extra={dateNode}
        headStyle={{ borderBottom: '1px solid rgba(0, 0, 0, 0.2)' }}
      >
        <Space direction="vertical" className="w-full">
          {showsSubject ? (
            <div>
              <div className="text-sm">Subject</div>
              <div className="text-xl">{parsed.subject || '(No subject)'}</div>
            </div>
          ) : null}
          {showsForwardedMarker && parsed.forwardedMessageCount > 0 ? (
            <Alert type="info" showIcon message={forwardedMarkerLabel(parsed.forwardedMessageCount)} />
          ) : null}
          <Divider className="m-0 mb-5" />
          {parsed.bodyHtml != null ? (
            <iframe title="Email body" sandbox="" srcDoc={parsed.bodyHtml} className="w-full min-h-[50vh] border-0" />
          ) : (
            <div className="whitespace-pre-wrap">{parsed.bodyText ?? ''}</div>
          )}
          <EmailAttachmentList attachments={parsed.attachments} />
        </Space>
      </Card>
    </div>
  );
}
